<template>
  <div class="history">
    <el-card class="panel">
      <template #header>
        <div class="card-header">
          <span><el-icon><Clock /></el-icon> 识别历史记录</span>
          <div class="header-actions">
            <el-button
                v-if="selectedBatchId"
                type="success"
                @click="exportBatch"
                :loading="exporting"
                size="small"
            >
              <el-icon><Download /></el-icon> 导出当前批次结果
            </el-button>
            <el-button text @click="resetSearch" :disabled="loading || batchLoading">
              <el-icon><Refresh /></el-icon>
              重置
            </el-button>
          </div>
        </div>
      </template>

      <!-- 搜索表单：任务名称 + 批次选择 -->
      <el-form :inline="true" class="search-form" @submit.prevent>
        <el-form-item label="任务名称">
          <el-input
              v-model="searchTaskName"
              placeholder="输入任务名称模糊搜索"
              clearable
              style="width: 220px;"
              @clear="handleTaskNameClear"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearchBatches" :loading="batchLoading">
            <el-icon><Search /></el-icon> 查询批次
          </el-button>
        </el-form-item>
        <el-form-item label="选择批次" v-if="batchOptions.length > 0">
          <el-select
              v-model="selectedBatchId"
              placeholder="请选择具体批次"
              clearable
              filterable
              @change="handleBatchChange"
              style="width: 300px;"
          >
            <el-option
                v-for="item in batchOptions"
                :key="item.batchId"
                :label="item.taskName + ' (' + formatTime(item.createTime) + ')'"
                :value="item.batchId"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="selectedBatchId">
          <el-tag type="info" closable @close="clearBatchSelection">当前筛选批次</el-tag>
        </el-form-item>
      </el-form>

      <!-- 批次统计卡片 -->
      <div v-if="selectedBatchId && batchStats" class="batch-stats">
        <el-card shadow="never" class="stats-card">
          <div class="stats-item">
            <span class="stats-label">总样本数：</span>
            <span class="stats-value">{{ batchStats.total }}</span>
          </div>
          <div class="stats-item">
            <span class="stats-label">已确认样本：</span>
            <span class="stats-value">{{ batchStats.confirmed }}</span>
          </div>
          <div class="stats-item">
            <span class="stats-label">正确数：</span>
            <span class="stats-value">{{ batchStats.correct }}</span>
          </div>
          <div class="stats-item">
            <span class="stats-label">错误数：</span>
            <span class="stats-value">{{ batchStats.incorrect }}</span>
          </div>
          <div class="stats-item" v-if="batchStats.confirmed > 0">
            <span class="stats-label">已确认准确率：</span>
            <span class="stats-value accuracy">{{ batchStats.accuracy }}%</span>
          </div>
          <div class="stats-item" v-else>
            <span class="stats-label">已确认准确率：</span>
            <span class="stats-value">无确认数据</span>
          </div>
        </el-card>
      </div>

      <!-- 数据表格 -->
      <el-table :data="tableData" v-loading="loading" style="width: 100%">
        <el-table-column label="图片" width="100">
          <template #default="{ row }">
            <img
                v-if="row.imagePath"
                :src="row.imagePath"
                class="table-image"
                @click="previewImage(row.imagePath)"
                @load="console.log('图片加载成功', $event.target.src)"
                @error="console.log('图片加载失败', $event.target.src, $event)"
            />
            <div v-else class="no-image">无图</div>
          </template>
        </el-table-column>
        <el-table-column prop="filename" label="文件名" min-width="180" show-overflow-tooltip />
        <el-table-column prop="result" label="识别结果" width="100" />
        <el-table-column prop="correctResult" label="人工修正" width="120">
          <template #default="{ row }">
            <span>{{ row.correctResult || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="是否正确" width="100">
          <template #default="{ row }">
            <el-tag :type="row.isCorrected === 1 ? 'success' : row.isCorrected === 0 ? 'danger' : 'info'">
              {{ row.isCorrected === 1 ? '正确' : row.isCorrected === 0 ? '错误' : '未确认' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="costTime" label="耗时(ms)" width="90" />
        <el-table-column prop="createTime" label="识别时间" width="160">
          <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="400" fixed="right">
          <template #default="{ row }">
            <el-button size="small" type="success" @click="handleMarkCorrect(row.id, 1)" :loading="markingId === row.id">正确</el-button>
            <el-button size="small" type="danger" @click="handleMarkCorrect(row.id, 0)" :loading="markingId === row.id">错误</el-button>
            <el-input
                v-model="correctInputs[row.id]"
                placeholder="修正值"
                size="small"
                style="width: 100px; margin: 0 5px;"
            />
            <el-button size="small" type="primary" @click="handleCorrectResult(row.id)" :loading="correctingId === row.id">提交</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 图片预览对话框 -->
    <el-dialog v-model="previewVisible" title="图片预览" width="30%" append-to-body destroy-on-close>
      <img :src="previewImageUrl" style="width: 100%;" />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import {
  getHistory,
  searchBatchesByName,
  getLogsByBatch,
  markCorrect,
  correctResult,
  downloadBatchResult,
  getBatchStats
} from '@/api/captcha'
import { ElMessage } from 'element-plus'
import {Clock, Download, Refresh, Search} from '@element-plus/icons-vue'

const route = useRoute()
const exporting = ref(false)

// 搜索条件
const searchTaskName = ref('')
const batchOptions = ref([])
const selectedBatchId = ref('')
const batchLoading = ref(false)

const loading = ref(false)
const tableData = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = ref(10)

// 标记/修正状态
const markingId = ref(null)
const correctingId = ref(null)
const correctInputs = ref({})

// 批次统计信息
const batchStats = ref(null)

// 图片预览
const previewVisible = ref(false)
const previewImageUrl = ref('')
const previewImage = (path) => {
  previewImageUrl.value = path
  previewVisible.value = true
}

const exportBatch = async () => {
  if (!selectedBatchId.value) {
    ElMessage.warning('请先选择一个批次')
    return
  }
  exporting.value = true
  try {
    const response = await downloadBatchResult(selectedBatchId.value)
    const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8;'})
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.href = url
    link.setAttribute('download', `${selectedBatchId.value}_results.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    ElMessage.success('导出成功')
  } catch (e) {
    console.error('导出失败', e)
    ElMessage.error('导出失败，请稍后重试')
  } finally {
    exporting.value = false
  }
}

// 辅助函数：安全提取数组和总数
const extractData = (responseData) => {
  if (Array.isArray(responseData)) {
    return {records: responseData, total: responseData.length}
  } else if (responseData && typeof responseData === 'object') {
    const records = responseData.records || responseData.data || []
    const totalCount = responseData.total || records.length || 0
    return {records: Array.isArray(records) ? records : [], total: totalCount}
  }
  return {records: [], total: 0}
}

// 获取所有历史记录
const fetchAllHistory = async () => {
  loading.value = true
  try {
    const resp = await getHistory(currentPage.value, pageSize.value)
    if (resp.data.code === 200) {
      const {records, total: totalCount} = extractData(resp.data.data)
      tableData.value = records
      total.value = totalCount
    } else {
      ElMessage.error(resp.data.msg || '获取历史记录失败')
      tableData.value = []
      total.value = 0
    }
  } catch (e) {
    console.error('fetchAllHistory error:', e)
    ElMessage.error('请求失败，请稍后重试')
    tableData.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

// 获取批次统计信息
const fetchBatchStats = async (batchId) => {
  try {
    const resp = await getBatchStats(batchId)
    if (resp.data.code === 200) {
      batchStats.value = resp.data.data
    } else {
      ElMessage.error(resp.data.msg || '获取批次统计失败')
    }
  } catch (e) {
    console.error('获取批次统计失败', e)
  }
}

// 根据批次号获取日志
const fetchLogsByBatch = async (batchId) => {
  loading.value = true
  try {
    const resp = await getLogsByBatch(batchId, currentPage.value, pageSize.value)
    if (resp.data.code === 200) {
      tableData.value = resp.data.data || []
      total.value = resp.data.total || 0
      await fetchBatchStats(batchId)
    } else {
      ElMessage.error(resp.data.msg || '获取批次日志失败')
      tableData.value = []
      total.value = 0
    }
  } catch (e) {
    ElMessage.error('请求失败')
    tableData.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

// 根据任务名称查询批次列表
const handleSearchBatches = async () => {
  if (!searchTaskName.value.trim()) {
    ElMessage.warning('请输入任务名称')
    return
  }
  batchLoading.value = true
  try {
    const resp = await searchBatchesByName({taskName: searchTaskName.value})
    if (resp.data.code === 200) {
      batchOptions.value = resp.data.data || []
      if (batchOptions.value.length === 0) {
        ElMessage.info('未找到相关批次')
      }
    } else {
      ElMessage.error(resp.data.msg || '查询批次失败')
    }
  } catch (e) {
    ElMessage.error('请求失败')
  } finally {
    batchLoading.value = false
  }
}

// 选择批次后查询日志
const handleBatchChange = (batchId) => {
  if (batchId) {
    currentPage.value = 1
    fetchLogsByBatch(batchId)
  } else {
    selectedBatchId.value = ''
    batchOptions.value = []
    batchStats.value = null
    fetchAllHistory()
  }
}

// 清空批次选择
const clearBatchSelection = () => {
  selectedBatchId.value = ''
  batchOptions.value = []
  batchStats.value = null
  fetchAllHistory()
}

// 清空任务名称输入时，隐藏批次下拉
const handleTaskNameClear = () => {
  batchOptions.value = []
}

// 重置所有筛选
const resetSearch = () => {
  searchTaskName.value = ''
  batchOptions.value = []
  selectedBatchId.value = ''
  batchStats.value = null
  currentPage.value = 1
  fetchAllHistory()
}

// 分页变化
const handleSizeChange = (val) => {
  pageSize.value = val
  if (selectedBatchId.value) {
    fetchLogsByBatch(selectedBatchId.value)
  } else {
    fetchAllHistory()
  }
}
const handleCurrentChange = (val) => {
  currentPage.value = val
  if (selectedBatchId.value) {
    fetchLogsByBatch(selectedBatchId.value)
  } else {
    fetchAllHistory()
  }
}

// 标记正确/错误
const handleMarkCorrect = async (logId, isCorrected) => {
  markingId.value = logId
  try {
    const resp = await markCorrect(logId, isCorrected)
    if (resp.data.code === 200) {
      ElMessage.success('标记成功')
      if (selectedBatchId.value) {
        await fetchLogsByBatch(selectedBatchId.value)
      } else {
        await fetchAllHistory()
      }
    } else {
      ElMessage.error(resp.data.msg || '标记失败')
    }
  } catch (e) {
    ElMessage.error('请求失败')
  } finally {
    markingId.value = null
  }
}

// 提交修正
const handleCorrectResult = async (logId) => {
  const userCorrect = correctInputs.value[logId]
  if (!userCorrect || userCorrect.trim() === '') {
    ElMessage.warning('请输入修正结果')
    return
  }
  correctingId.value = logId
  try {
    const resp = await correctResult(logId, userCorrect.trim())
    if (resp.data.code === 200) {
      ElMessage.success('修正成功')
      correctInputs.value[logId] = ''
      if (selectedBatchId.value) {
        await fetchLogsByBatch(selectedBatchId.value)
      } else {
        await fetchAllHistory()
      }
    } else {
      ElMessage.error(resp.data.msg || '修正失败')
    }
  } catch (e) {
    ElMessage.error('请求失败')
  } finally {
    correctingId.value = null
  }
}

// 格式化时间
const formatTime = (timeStr) => {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

// 如果路由中带有 batchId，自动选中并查询
watch(() => route.query.batchId, (newVal) => {
  if (newVal) {
    selectedBatchId.value = newVal
    fetchLogsByBatch(newVal)
  } else {
    if (!selectedBatchId.value) {
      fetchAllHistory()
    }
  }
}, {immediate: true})

// 初始加载
onMounted(() => {
  if (!route.query.batchId) {
    fetchAllHistory()
  }
})
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.history {
  padding: 20px;
}

.panel {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
}

.search-form {
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.batch-stats {
  margin-bottom: 20px;
}

.stats-card {
  background-color: #f8f9fa;
  padding: 10px 15px;
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.stats-item {
  font-size: 14px;
}

.stats-label {
  color: #606266;
  font-weight: normal;
}

.stats-value {
  font-weight: bold;
  color: #303133;
}

.stats-value.accuracy {
  color: #409eff;
  font-size: 16px;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.no-image {
  width: 50px;
  height: 50px;
  background-color: #f5f7fa;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #909399;
}

.table-image {
  width: 100%;
  height: auto;
  max-height: 50px;
  object-fit: contain;
  border-radius: 4px;
  cursor: pointer;
}
</style>